import { gql, useQuery } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useContext } from 'react';
import CollectionView from 'src/CollectionsPage/CollectionView';
import { ShareCollection } from 'src/dynamoQueries/collections';
import { SharedGalleryTopNavBar } from 'src/HomePage/TopNavBar/SharedGalleryNav';
import TopNavBar from 'src/HomePage/TopNavBar/TopNavBar';
import SharedGalleryMediaPage from 'src/Media/SharedGalleryMediaPage';
import { SharedLinkContext, type SharedLinkInfo } from '../context';
import SharedGalleryPage from './SharedGalleryPage';

type ComponentType = 'GALLERY-MEDIA' | 'GALLERY' | 'PLAYLIST';

interface SwitcherComponentProps {
  componentType: ComponentType | string;
  linkInfo: SharedLinkInfo;
}

interface SharedCollectionData {
  sharedCollection: {
    collectionType: string;
  };
}

const SwitcherComponent = ({ componentType, linkInfo }: SwitcherComponentProps): React.JSX.Element | null => {
  switch (componentType) {
    case 'GALLERY-MEDIA':
      return (
        <>
          <TopNavBar simple />
          <SharedGalleryMediaPage />
        </>
      );
    case 'GALLERY':
      return (
        <>
          <SharedGalleryTopNavBar />
          <SharedGalleryPage />
        </>
      );
    case 'PLAYLIST':
      return (
        <>
          <SharedGalleryTopNavBar />
          <CollectionView
            collectionId={linkInfo.collectionId || ''}
            shared={true}
            downloadable={!!linkInfo.downloadable}
          />
        </>
      );
    default:
      return <div>This Collection Type cannot be shared</div>;
  }
};

interface SharedLinkCollectionSwitchComponentProps {
  mediaPage?: boolean;
}

const SharedLinkCollectionSwitchComponent = ({
  mediaPage,
}: SharedLinkCollectionSwitchComponentProps): React.JSX.Element => {
  const { linkInfo } = useContext(SharedLinkContext);

  const buildQuery = (): ReturnType<typeof gql> => {
    const query = ShareCollection(true);
    return gql(jsonToGraphQLQuery(query, { pretty: true }));
  };

  const { data, loading, error } = useQuery<SharedCollectionData>(buildQuery(), {
    variables: { id: linkInfo.collectionId },
  });

  if (loading)
    return (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );

  if (error) return <p>An error occurred - {error.message}</p>;

  const componentType = mediaPage ? 'GALLERY-MEDIA' : data?.sharedCollection?.collectionType || '';

  return <SwitcherComponent componentType={componentType} linkInfo={linkInfo} />;
};

export default SharedLinkCollectionSwitchComponent;
